import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  ArrowBackRounded,
  GroupsRounded,
  WarningAmberRounded,
  FlagRounded,
  FileDownloadRounded,
  ForumRounded,
  ScheduleRounded,
  MenuBookRounded,
} from '@mui/icons-material';
import {
  ReinforcementStudent,
  DashboardClassSummary,
  DashboardStudentSnapshot,
} from '../types';
import {
  getClassReinforcementReport,
  getClassDashboardMetrics,
  getSyllabusProgress,
  analyzeClassConversations,
  ReinforcementTopic,
} from '../services/api';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import { resolveAnalysisText } from '../utils/analysisText';
import { writeSummarySection } from '../utils/reportPdf';
import { resolveTeacherClassContext } from '../utils/teacherClassContext';
import TeacherLayout from './teacher/TeacherLayout';
import SubjectHealthRows, {
  classHealthBySubject,
  rollupBucket,
  subjectLabel,
  distinctSubjectLabels,
} from './teacher/SubjectHealthRows';
import DateRangeControl, { DateRange, rangeForDays } from './teacher/DateRangeControl';

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths', science: 'Science', social: 'Social', global: 'Your topics',
};

const CATEGORY_LABELS: Record<string, string> = {
  bright: 'Ahead',
  hardworking: 'On track',
  disengaged: 'Needs support',
  unknown: 'No data yet',
};


interface ClassReportState {
  school?: string;
  grade?: number | string;
  section?: string | null;
}

const ClassReport: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useTeacherAuth();
  const state = (location.state as ClassReportState) || {};
  const classContext = resolveTeacherClassContext(state);
  const school = classContext.school;
  const gradeNum =
    typeof classContext.grade === 'string' ? Number(classContext.grade) : classContext.grade;
  const section = classContext.section ?? null;
  const backState = { school, grade: classContext.grade, section: section ?? undefined };

  const [range, setRange] = useState<DateRange>(() => rangeForDays(90));
  // Subject drill-down layered under class level. null = "All subjects" (class
  // level). Otherwise a subject LABEL (e.g. "Science", "Physics") — everything
  // subject-tagged re-scopes to it client-side; nothing is refetched.
  const [activeSubject, setActiveSubject] = useState<string | null>(null);
  const [students, setStudents] = useState<ReinforcementStudent[]>([]);
  const [topics, setTopics] = useState<ReinforcementTopic[]>([]);
  const [summary, setSummary] = useState<DashboardClassSummary | null>(null);
  const [prevSummary, setPrevSummary] = useState<DashboardClassSummary | null>(null);
  const [snapshots, setSnapshots] = useState<DashboardStudentSnapshot[]>([]);
  const [exporting, setExporting] = useState(false);
  // Count of curriculum sub-topics the teacher has ticked as covered for this class.
  const [syllabusCount, setSyllabusCount] = useState<number>(0);

  useEffect(() => {
    if (!token || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    let on = true;
    getClassReinforcementReport(
      token, Number(gradeNum), section, false, range.start || undefined, range.end || undefined, school
    )
      .then((r) => {
        if (!on) return;
        setStudents(r.report_data?.students ?? []);
        setTopics(r.report_data?.topics ?? []);
      })
      .catch(() => { if (on) { setStudents([]); setTopics([]); } });
    return () => { on = false; };
  }, [token, school, gradeNum, section, range.start, range.end]);

  useEffect(() => {
    if (!school || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    let on = true;
    getClassDashboardMetrics(
      school, Number(gradeNum), section, range.start || undefined, range.end || undefined
    )
      .then((r) => {
        if (!on) return;
        setSummary(r.class_summary);
        setSnapshots(r.student_snapshots ?? []);
      })
      .catch(() => {
        if (!on) return;
        setSummary(null); setSnapshots([]);
      });
    return () => { on = false; };
  }, [school, gradeNum, section, range.start, range.end]);

  // Previous same-length window — powers the "vs last Nd" deltas on the tiles.
  useEffect(() => {
    if (!school || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    if (!range.start || !range.end) { setPrevSummary(null); return; } // "All" has no prior period
    const toD = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const s = toD(range.start), e = toD(range.end);
    const lenDays = Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
    const prevEnd = new Date(s); prevEnd.setDate(prevEnd.getDate() - 1);
    const prevStart = new Date(prevEnd); prevStart.setDate(prevStart.getDate() - (lenDays - 1));
    let on = true;
    getClassDashboardMetrics(school, Number(gradeNum), section, iso(prevStart), iso(prevEnd))
      .then((r) => { if (on) setPrevSummary(r.class_summary); })
      .catch(() => { if (on) setPrevSummary(null); });
    return () => { on = false; };
  }, [school, gradeNum, section, range.start, range.end]);

  // Accurate "vs last Nd" deltas from the current vs previous window totals.
  const deltas = useMemo(() => {
    if (!summary || !prevSummary || !range.start || !range.end) return null;
    const toT = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d).getTime(); };
    const nDays = Math.round((toT(range.end) - toT(range.start)) / 86400000) + 1;
    const mk = (cur?: number | null, prev?: number | null) => {
      const c = cur ?? 0, p = prev ?? 0;
      if (p === 0 || c === p) return null;
      return { up: c > p, text: `${Math.abs(Math.round(((c - p) / p) * 100))}% vs last ${nDays}d` };
    };
    return {
      sessions: mk(summary.total_conversations, prevSummary.total_conversations),
      minutes: mk(
        summary.avg_minutes_per_active_student_day,
        prevSummary.avg_minutes_per_active_student_day,
      ),
    };
  }, [summary, prevSummary, range.start, range.end]);

  // Curriculum coverage: how many sub-topics the class has ticked as covered.
  // Not date-windowed (it's a cumulative teacher-marked list).
  useEffect(() => {
    if (!token || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    let on = true;
    getSyllabusProgress(token, Number(gradeNum), section)
      .then((r) => { if (on) setSyllabusCount((r.items ?? []).length); })
      .catch(() => { if (on) setSyllabusCount(0); });
    return () => { on = false; };
  }, [token, gradeNum, section]);

  const classTitle = [
    school,
    gradeNum != null && !Number.isNaN(Number(gradeNum)) ? `Grade ${gradeNum}` : null,
    section ? `Section ${section}` : null,
  ].filter(Boolean).join(' · ');

  const totalStudents =
    summary?.total_students ?? Math.max(students.length, snapshots.length, 0);

  // Subjects present anywhere in the subject-tagged data — powers the filter bar.
  const availableSubjects = useMemo(() => {
    const recs: Array<{ subject?: string | null }> = [];
    students.forEach((s) => (s.topics || []).forEach((t) => recs.push({ subject: t.subject })));
    topics.forEach((t) => recs.push({ subject: t.subject }));
    return distinctSubjectLabels(recs);
  }, [students, topics]);

  // A subject chip can vanish when the date range changes — fall back to "All".
  useEffect(() => {
    if (activeSubject != null && !availableSubjects.includes(activeSubject)) {
      setActiveSubject(null);
    }
  }, [availableSubjects, activeSubject]);

  /** True when a topic belongs to the active subject (or no filter is on). */
  const matchesActive = useCallback(
    (subject?: string | null) => activeSubject == null || subjectLabel(subject) === activeSubject,
    [activeSubject]
  );

  /** A student's struggled-topic count within the active scope. */
  const struggleInScope = useCallback(
    (s: ReinforcementStudent) =>
      activeSubject == null
        ? s.struggled_count
        : (s.topics || []).filter((t) => t.struggled && subjectLabel(t.subject) === activeSubject)
            .length,
    [activeSubject]
  );

  // "Needs support": disengaged students class-wide; when a subject is picked,
  // students with any struggle in that subject.
  const needsFoundations = useMemo(() => {
    if (activeSubject == null) return students.filter((s) => s.category === 'disengaged').length;
    return students.filter((s) =>
      (s.topics || []).some((t) => t.struggled && subjectLabel(t.subject) === activeSubject)
    ).length;
  }, [students, activeSubject]);

  const attentionTopics = useMemo(
    () =>
      [...topics]
        .filter((t) => t.struggled > 0 && matchesActive(t.subject))
        .sort((a, b) => b.struggled - a.struggled)
        .slice(0, 6),
    [topics, matchesActive]
  );

  // Most-explored topics across the class, ranked by how many students touched
  // each. Sourced from report_data.topics, which is built from real chats
  // (conversations.core_chat_theme). Replaces the old summary.top_topics feed,
  // which came from the unpopulated conversation_evaluations table and always
  // rendered empty.
  const mostExplored = useMemo(
    () => [...topics].sort((a, b) => b.n_students - a.n_students),
    [topics]
  );
  // Athira #2: split most-explored topics into those from the pushed curriculum
  // vs. ones students explored on their own (top 8 of each).
  const curriculumTopics = useMemo(
    () => mostExplored.filter((t) => t.from_curriculum).slice(0, 8),
    [mostExplored]
  );
  const selfExploredTopics = useMemo(
    () => mostExplored.filter((t) => !t.from_curriculum).slice(0, 8),
    [mostExplored]
  );

  // Per-subject health rows; narrowed to the one subject when filtered.
  const subjectHealth = useMemo(() => {
    const all = classHealthBySubject(students);
    return activeSubject == null ? all : all.filter((g) => g.label === activeSubject);
  }, [students, activeSubject]);

  // Class-wide mastery mix (each student counted once, rolled up across topics)
  // for the overall donut above the per-subject bars. When filtered, only
  // students who have touched the subject are counted, over that subject's topics.
  const overallMastery = useMemo(() => {
    const c: Record<string, number> = { got: 0, partly: 0, notyet: 0, explored: 0 };
    students.forEach((s) => {
      const scoped =
        activeSubject == null
          ? s.topics || []
          : (s.topics || []).filter((t) => subjectLabel(t.subject) === activeSubject);
      if (activeSubject != null && scoped.length === 0) return;
      const b = rollupBucket(scoped.map((t) => t.state));
      c[b] = (c[b] ?? 0) + 1;
    });
    return c;
  }, [students, activeSubject]);

  // Three-way understanding mix for the "Overall understanding" donut.
  const understandingMix = useMemo(
    () => ({
      understood: overallMastery.got ?? 0,
      almost: overallMastery.partly ?? 0,
      needs: (overallMastery.notyet ?? 0) + (overallMastery.explored ?? 0),
    }),
    [overallMastery]
  );

  const attentionStudents = useMemo(
    () =>
      students
        .map((s) => ({ ...s, scopeStruggle: struggleInScope(s) }))
        .filter((s) => s.scopeStruggle > 0)
        .sort((a, b) => b.scopeStruggle - a.scopeStruggle)
        .slice(0, 5),
    [students, struggleInScope]
  );

  const notStartedCount = useMemo(() => {
    if (totalStudents === 0) return 0;
    const active = snapshots.filter((s) => (s.total_user_messages ?? 0) > 0).length;
    return Math.max(0, totalStudents - active);
  }, [snapshots, totalStudents]);

  const nextActions = useMemo<string[]>(() => {
    const out: string[] = [];
    const top = attentionTopics[0];
    if (top) out.push(`Revisit ${top.topic} — ${top.struggled} of ${top.n_students} students are stuck.`);
    const stu = attentionStudents[0];
    if (stu)
      out.push(
        `Check in with ${stu.name} — struggling with ${stu.scopeStruggle} topic${stu.scopeStruggle === 1 ? '' : 's'}${activeSubject ? ` in ${activeSubject}` : ''}.`
      );
    if (notStartedCount > 0)
      out.push(
        `${notStartedCount} student${notStartedCount === 1 ? '' : 's'} haven't used the coach yet — get them a first session.`
      );
    if (needsFoundations > 0)
      out.push(
        `${needsFoundations} student${needsFoundations === 1 ? '' : 's'} need foundational support before the next topic.`
      );
    return out.slice(0, 4);
  }, [attentionTopics, attentionStudents, notStartedCount, needsFoundations, activeSubject]);

  const handleDownload = async () => {
    if (exporting) return;
    if (!school || gradeNum == null || Number.isNaN(Number(gradeNum))) return;
    setExporting(true);
    try {
      // Generate the whole-class LLM narrative first (may queue a job and poll).
      let narrative: string | null = null;
      try {
        narrative = await resolveAnalysisText(
          () => analyzeClassConversations(school, Number(gradeNum), section)
        );
      } catch {
        narrative = null;
      }

      const doc = new jsPDF();
      const mx = 14;
      const contentW = 182;
      let y = 18;
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Class Report', mx, y);
      y += 7;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120);
      doc.text(activeSubject ? `${classTitle} · ${activeSubject}` : classTitle, mx, y);
      y += 9;
      doc.setTextColor(30);

      // Written summary — the coach's LLM narrative (always whole-class).
      y = writeSummarySection(
        doc,
        activeSubject ? 'Summary (whole class)' : 'Summary',
        narrative,
        mx,
        y,
        contentW
      );

      autoTable(doc, {
        startY: y,
        head: [['Students', 'Needs support']],
        body: [[String(totalStudents), String(needsFoundations)]],
        theme: 'grid',
        headStyles: { fillColor: [124, 58, 237] },
        styles: { fontSize: 9, halign: 'center' },
      });
      y = (doc as any).lastAutoTable.finalY + 8;

      if (nextActions.length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['What to do next']],
          body: nextActions.map((a) => [a]),
          theme: 'plain',
          headStyles: { fontStyle: 'bold', textColor: [124, 58, 237] },
          styles: { fontSize: 10 },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      if (subjectHealth.length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['Subject', 'Got it', 'Almost', 'Needs practice', 'Explored']],
          body: subjectHealth.map((g) => [g.label, String(g.got), String(g.partly), String(g.notyet), String(g.explored)]),
          theme: 'striped',
          headStyles: { fillColor: [124, 58, 237] },
          styles: { fontSize: 9 },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      if (attentionTopics.length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['Concept needing reinforcement', 'Struggling', '%']],
          body: attentionTopics.map((t) => {
            const pct = t.n_students > 0 ? Math.round((t.struggled / t.n_students) * 100) : 0;
            const label = t.subject ? `${t.topic} (${SUBJECT_LABELS[t.subject] || t.subject})` : t.topic;
            return [label, `${t.struggled}/${t.n_students}`, `${pct}%`];
          }),
          theme: 'striped',
          headStyles: { fillColor: [124, 58, 237] },
          styles: { fontSize: 9 },
        });
        y = (doc as any).lastAutoTable.finalY + 8;
      }

      // Students table — scoped to the active subject when filtered.
      const studentRows = students
        .map((s) => ({ s, struggle: struggleInScope(s) }))
        .filter(({ struggle }) => activeSubject == null || struggle > 0)
        .sort((a, b) => b.struggle - a.struggle);
      if (studentRows.length > 0) {
        autoTable(doc, {
          startY: y,
          head: [['Student', 'Level', activeSubject ? `Struggled (${activeSubject})` : 'Struggled topics']],
          body: studentRows.map(({ s, struggle }) => [
            s.name,
            CATEGORY_LABELS[s.category] ?? s.category,
            String(struggle),
          ]),
          theme: 'grid',
          headStyles: { fillColor: [124, 58, 237] },
          styles: { fontSize: 9 },
        });
      }

      doc.save(`Class report - ${classTitle}${activeSubject ? ` (${activeSubject})` : ''}.pdf`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <TeacherLayout active="report">
      <div className="mx-auto w-full max-w-5xl space-y-5">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/teacher-dashboard', { state: backState })}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#E9E6F4] bg-white text-slate-600 hover:border-[#D9D3EE]"
            >
              <ArrowBackRounded sx={{ fontSize: 18 }} />
            </button>
            <div>
              <p className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-slate-400">Class report</p>
              <h1 className="text-xl font-bold tracking-tight text-slate-900">{classTitle}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <DateRangeControl value={range} onChange={setRange} includeAllTime />
            <button
              type="button"
              onClick={handleDownload}
              disabled={exporting}
              className="inline-flex items-center gap-1 rounded-xl bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
            >
              <FileDownloadRounded sx={{ fontSize: 17 }} /> {exporting ? 'Preparing…' : 'Export'}
            </button>
          </div>
        </div>

        {/* Subject filter — "All subjects" (class level) + one chip per subject. */}
        {availableSubjects.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="mr-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Subject
            </span>
            <SubjectChip label="All subjects" active={activeSubject == null} onClick={() => setActiveSubject(null)} />
            {availableSubjects.map((s) => (
              <SubjectChip key={s} label={s} active={activeSubject === s} onClick={() => setActiveSubject(s)} />
            ))}
          </div>
        )}

        {/* Stat tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Tile
            icon={<GroupsRounded sx={{ fontSize: 20 }} />}
            tint="bg-violet-100 text-violet-700"
            value={String(totalStudents)}
            label="Students"
            note={activeSubject ? 'all subjects' : undefined}
          />
          <Tile
            icon={<ForumRounded sx={{ fontSize: 20 }} />}
            tint="bg-sky-100 text-sky-700"
            value={String(summary?.total_conversations ?? 0)}
            label="Sessions"
            delta={deltas?.sessions}
          />
          <Tile
            icon={<ScheduleRounded sx={{ fontSize: 20 }} />}
            tint="bg-emerald-100 text-emerald-700"
            value={String(Math.round(summary?.avg_minutes_per_active_student_day ?? 0))}
            label="Minutes per student, per active day"
            delta={deltas?.minutes}
          />
          <Tile
            icon={<MenuBookRounded sx={{ fontSize: 20 }} />}
            tint="bg-indigo-100 text-indigo-700"
            value={String(syllabusCount)}
            label="Topics completed"
          />
          <Tile
            icon={<WarningAmberRounded sx={{ fontSize: 20 }} />}
            tint="bg-amber-100 text-amber-700"
            value={String(needsFoundations)}
            label={activeSubject ? `Struggling in ${activeSubject}` : 'Needs support'}
          />
        </div>

        {/* What to do next */}
        {nextActions.length > 0 && (
          <Card title="What to do next" sub="Where your attention helps the class most">
            <ol className="space-y-2.5">
              {nextActions.map((a, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-700">
                  <span className="flex h-6 w-6 flex-none items-center justify-center rounded-lg bg-violet-100 text-[13px] font-bold text-violet-700">
                    {i + 1}
                  </span>
                  <span className="pt-0.5">{a}</span>
                </li>
              ))}
            </ol>
          </Card>
        )}

        {/* Subject performance + Students needing attention */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card
            title={activeSubject ? `How ${activeSubject} is going` : 'How each subject is going'}
            sub={activeSubject ? `Students by mastery in ${activeSubject}` : 'Students by mastery, per subject'}
          >
            {subjectHealth.length === 0 ? (
              <Empty>No subject data yet.</Empty>
            ) : (
              <>
                <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
                  <MasteryDonut counts={overallMastery} />
                  <div className="w-full min-w-0 flex-1">
                    <SubjectHealthRows groups={subjectHealth} />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500">
                  <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-emerald-500 align-middle" />Got it</span>
                  <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-amber-500 align-middle" />Almost</span>
                  <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-rose-400 align-middle" />Needs practice</span>
                  <span><span className="mr-1 inline-block h-2 w-2 rounded-sm bg-slate-300 align-middle" />Explored</span>
                </div>
              </>
            )}
          </Card>

          <Card
            title="Students needing attention"
            sub={activeSubject ? `Most to catch up on in ${activeSubject}` : 'Most topics to catch up on'}
          >
            {attentionStudents.length === 0 ? (
              <Empty>{activeSubject ? `No one flagged in ${activeSubject}.` : 'No one flagged right now.'}</Empty>
            ) : (
              <div className="flex flex-col gap-1">
                {attentionStudents.map((s) => (
                  <div key={s.student_id} className="flex items-center gap-3 rounded-lg px-2 py-2">
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white">
                      {s.name.trim().slice(0, 1).toUpperCase()}
                    </span>
                    <span className="flex-1 truncate text-sm font-medium text-slate-700">{s.name}</span>
                    <span className="text-xs font-semibold text-slate-500">
                      {s.scopeStruggle} topic{s.scopeStruggle === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* What the class is curious about — most-explored topics from real chats.
            Stays class-wide (all subjects) per #77's design; only the caption
            notes it when a subject filter is active. */}
        <Card
          title="What the class is curious about"
          sub={activeSubject ? 'Most-explored topics · across all subjects' : 'Topics the most students explored with the coach'}
        >
          {mostExplored.length === 0 ? (
            <Empty>Topics show up here as students chat.</Empty>
          ) : (
            <div className="space-y-3">
              {[
                { label: 'From the curriculum', items: curriculumTopics, pill: 'bg-violet-50 text-violet-800', dot: 'text-violet-400' },
                { label: 'Explored on their own', items: selfExploredTopics, pill: 'bg-sky-50 text-sky-800', dot: 'text-sky-400' },
              ].filter((g) => g.items.length > 0).map((g) => (
                <div key={g.label}>
                  <p className="mb-1.5 text-xs font-semibold text-slate-500">{g.label}</p>
                  <div className="flex flex-wrap gap-2">
                    {g.items.map((t) => (
                      <span key={`${t.subject ?? ''}:${t.topic}`} className={`rounded-full px-3 py-1 text-sm font-medium ${g.pill}`}>
                        {t.topic}
                        <span className={g.dot}> · {t.n_students}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Concepts needing reinforcement + Overall understanding */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
        <Card
          title="Concepts needing reinforcement"
          sub={activeSubject ? `Where the class is struggling most in ${activeSubject}` : 'Where the class is struggling most'}
        >
          {attentionTopics.length === 0 ? (
            <Empty>{activeSubject ? `No struggle signals in ${activeSubject} yet.` : 'No struggle signals yet.'}</Empty>
          ) : (
            <div className="space-y-2.5">
              {attentionTopics.map((t) => {
                const pct = t.n_students > 0 ? Math.round((t.struggled / t.n_students) * 100) : 0;
                const color = t.flagged ? '#fb7185' : pct >= 33 ? '#f59e0b' : '#10b981';
                const subj = t.subject ? SUBJECT_LABELS[t.subject] || t.subject : null;
                return (
                  <div key={`${t.subject ?? ''}:${t.topic}`}
                    className={`flex items-center gap-4 rounded-xl border p-3 ${t.flagged ? 'border-rose-100 bg-rose-50/40' : 'border-[#E9E6F4]'}`}>
                    <div className="w-24 flex-none">
                      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                      </div>
                      <p className="mt-1 text-[11px] font-semibold text-slate-500">{pct}% struggling</p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-bold text-slate-900">{t.topic}</p>
                      <p className="text-xs text-slate-500">
                        {t.struggled} of {t.n_students} students{subj ? ` · ${subj}` : ''}
                      </p>
                    </div>
                    {t.flagged ? (
                      <span className="flex-none rounded-full bg-rose-100 px-2.5 py-1 text-[0.67rem] font-bold text-rose-700">Revision needed</span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
          </div>
          <div className="lg:col-span-1">
            <Card title="Overall understanding" sub="Across all topics">
              <div className="flex flex-col items-center gap-3">
                <UnderstandingDonut
                  understood={understandingMix.understood}
                  almost={understandingMix.almost}
                  needs={understandingMix.needs}
                />
                <div className="w-full space-y-1.5 text-sm">
                  {[
                    { color: '#10b981', label: 'Understood', n: understandingMix.understood },
                    { color: '#f59e0b', label: 'Almost there', n: understandingMix.almost },
                    { color: '#fb7185', label: 'Needs practice', n: understandingMix.needs },
                  ].map((row) => {
                    const total = understandingMix.understood + understandingMix.almost + understandingMix.needs;
                    const pct = total > 0 ? Math.round((row.n / total) * 100) : 0;
                    return (
                      <div key={row.label} className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2 text-slate-600">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: row.color }} />
                          {row.label}
                        </span>
                        <span className="font-semibold text-slate-500">{pct}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Reinforce a student */}
        <button
          type="button"
          onClick={() => navigate('/class-details', { state: backState })}
          className="flex w-full items-center gap-4 rounded-2xl border border-[#E9E6F4] bg-white p-5 text-left hover:border-violet-200 hover:bg-violet-50/40"
        >
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-violet-100 text-violet-700">
            <FlagRounded sx={{ fontSize: 22 }} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold text-slate-900">Reinforce a student</span>
            <span className="mt-0.5 block text-xs text-slate-500">
              Open the class list to review a student and flag topics for the coach.
            </span>
          </span>
          <span className="text-lg text-slate-400">→</span>
        </button>
      </div>
    </TeacherLayout>
  );
};

const Tile: React.FC<{
  icon: React.ReactNode;
  tint: string;
  value: string;
  label: string;
  note?: string;
  delta?: { up: boolean; text: string } | null;
}> = ({ icon, tint, value, label, note, delta }) => (
  <div className="rounded-2xl border border-[#E9E6F4] bg-white px-4 py-4">
    <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${tint}`}>{icon}</span>
    <p className="mt-2.5 text-xl font-bold tracking-tight text-slate-900">{value}</p>
    <p className="text-xs font-medium text-slate-500">
      {label}
      {note ? <span className="text-slate-400"> · {note}</span> : null}
    </p>
    {delta && (
      <p className={`mt-1 text-[11px] font-semibold ${delta.up ? 'text-emerald-600' : 'text-rose-500'}`}>
        {delta.up ? '↑' : '↓'} {delta.text}
      </p>
    )}
  </div>
);

const UnderstandingDonut: React.FC<{ understood: number; almost: number; needs: number }> = ({ understood, almost, needs }) => {
  const total = understood + almost + needs;
  const pct = total > 0 ? Math.round((understood / total) * 100) : 0;
  const r = 40, c = 2 * Math.PI * r, sw = 11;
  let off = 0;
  const segs = [
    { v: understood, color: '#10b981' },
    { v: almost, color: '#f59e0b' },
    { v: needs, color: '#fb7185' },
  ];
  return (
    <svg viewBox="0 0 100 100" className="h-28 w-28">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#eef1f6" strokeWidth={sw} />
      {total > 0 &&
        segs.map((s, i) => {
          if (!s.v) return null;
          const len = (s.v / total) * c;
          const el = (
            <circle
              key={i}
              cx="50"
              cy="50"
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={sw}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-off}
              transform="rotate(-90 50 50)"
            />
          );
          off += len;
          return el;
        })}
      <text x="50" y="49" textAnchor="middle" fontSize="20" fontWeight="700" fill="#0f172a">{pct}%</text>
      <text x="50" y="63" textAnchor="middle" fontSize="8.5" fill="#94a3b8">Understood</text>
    </svg>
  );
};

const SubjectChip: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-colors ${
      active
        ? 'border-violet-600 bg-violet-600 text-white'
        : 'border-[#E9E6F4] bg-white text-slate-600 hover:border-violet-300 hover:bg-violet-50'
    }`}
  >
    {label}
  </button>
);

const MASTERY_SEGS: { key: string; color: string }[] = [
  { key: 'got', color: '#10b981' },
  { key: 'partly', color: '#f59e0b' },
  { key: 'notyet', color: '#fb7185' },
  { key: 'explored', color: '#cbd5e1' },
];

/** Class-wide mastery donut — each student counted once. */
const MasteryDonut: React.FC<{ counts: Record<string, number> }> = ({ counts }) => {
  const total = MASTERY_SEGS.reduce((n, s) => n + (counts[s.key] || 0), 0);
  const r = 34;
  const c = 2 * Math.PI * r;
  const sw = 12;
  let off = 0;
  return (
    <svg viewBox="0 0 90 90" className="h-24 w-24 flex-none">
      <circle cx="45" cy="45" r={r} fill="none" stroke="#eef1f6" strokeWidth={sw} />
      {total > 0 &&
        MASTERY_SEGS.map((s) => {
          const v = counts[s.key] || 0;
          if (v === 0) return null;
          const len = (v / total) * c;
          const el = (
            <circle
              key={s.key}
              cx="45"
              cy="45"
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth={sw}
              strokeDasharray={`${len} ${c - len}`}
              strokeDashoffset={-off}
              transform="rotate(-90 45 45)"
            />
          );
          off += len;
          return el;
        })}
      <text x="45" y="43" textAnchor="middle" fontSize="18" fontWeight="700" fill="#0f172a">
        {total}
      </text>
      <text x="45" y="57" textAnchor="middle" fontSize="8" fill="#94a3b8">
        students
      </text>
    </svg>
  );
};

const Card: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <section className="rounded-2xl border border-[#E9E6F4] bg-white px-5 py-4 shadow-sm">
    <div className="mb-3">
      <h2 className="text-base font-bold text-slate-900">{title}</h2>
      {sub ? <p className="text-xs text-slate-500">{sub}</p> : null}
    </div>
    {children}
  </section>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="rounded-xl bg-slate-50 px-3 py-3 text-sm text-slate-500">{children}</p>
);

export default ClassReport;
